
import React from 'react';
import { MousePointerClick, Star, Trophy, GamepadIcon } from 'lucide-react';

const GameDescription = () => {
  return (
    <section id="game-description" className="py-10">
      <div className="bg-indigo-800 bg-opacity-50 rounded-xl p-8 shadow-xl">
        <h2 className="text-3xl font-bold mb-6 text-center text-yellow-300">About Capybara Clicker 3D</h2>
        
        <div className="space-y-6 text-indigo-100">
          <p className="text-lg leading-relaxed">
            <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> is an engaging and addictive incremental game that puts you in charge of building a thriving capybara empire. This free online game combines the satisfaction of clicker mechanics with cute capybara characters in a colorful 3D environment.
          </p>
          
          <p className="text-lg leading-relaxed">
            In the world of <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>, you start with a single capybara and your trusty mouse button. Each click represents your effort to grow your population of these adorable semi-aquatic rodents. As you continue clicking, your capybara numbers will increase, allowing you to unlock various upgrades and features that make your clicking more efficient.
          </p>
          
          <div className="bg-indigo-900 bg-opacity-50 p-6 rounded-lg my-8">
            <h3 className="text-xl font-bold mb-4 text-yellow-200 flex items-center">
              <GamepadIcon className="mr-2 h-6 w-6" />
              Game Highlights
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Simple yet addictive gameplay mechanic - just click to multiply your capybaras</li>
              <li>Beautiful 3D graphics with cute capybara characters</li>
              <li>Multiple upgrades to enhance your clicking power</li>
              <li>Customize your capybaras with different outfits and accessories</li>
              <li>Change the environment and weather as you progress</li>
              <li>Perfect for casual gamers of all ages</li>
              <li>Completely free to play online</li>
              <li>No download required - play directly in your browser</li>
            </ul>
          </div>
          
          <p className="text-lg leading-relaxed">
            What makes <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> special is its charming approach to the incremental game genre. Capybaras, known as nature's most relaxed animals, bring a sense of calm and joy to the clicking experience. The game's 3D environment allows you to view your growing capybara collection from different angles, appreciating their cute behaviors and interactions.
          </p>
          
          <p className="text-lg leading-relaxed">
            As you progress in <span className="font-semibold text-pink-300">Capybara Clicker 3D</span>, you'll unlock various achievements and milestones. The game keeps track of your clicking statistics and rewards you for reaching specific goals. This creates a satisfying progression system that keeps you engaged for hours of clicking fun.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center my-8">
            <div className="bg-purple-900 bg-opacity-50 p-6 rounded-lg flex-1">
              <div className="flex items-center mb-4">
                <MousePointerClick className="h-8 w-8 text-purple-300 mr-3" />
                <h3 className="text-xl font-bold text-purple-300">Click Power</h3>
              </div>
              <p>Increase your clicking efficiency with powerful upgrades that multiply your clicks' value.</p>
            </div>
            
            <div className="bg-purple-900 bg-opacity-50 p-6 rounded-lg flex-1">
              <div className="flex items-center mb-4">
                <Star className="h-8 w-8 text-yellow-300 mr-3" />
                <h3 className="text-xl font-bold text-yellow-300">Customization</h3>
              </div>
              <p>Dress your capybaras in different outfits and accessories to create your unique capybara paradise.</p>
            </div>
            
            <div className="bg-purple-900 bg-opacity-50 p-6 rounded-lg flex-1">
              <div className="flex items-center mb-4">
                <Trophy className="h-8 w-8 text-pink-300 mr-3" />
                <h3 className="text-xl font-bold text-pink-300">Achievements</h3>
              </div>
              <p>Complete challenges and earn special rewards as you build your capybara empire.</p>
            </div>
          </div>
          
          <p className="text-lg leading-relaxed">
            The developers of <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> have created a game that appeals to casual players who enjoy relaxing gameplay sessions while also providing depth for those who want to optimize their clicking strategy. The balance between simplicity and strategic depth makes this game accessible to players of all experience levels.
          </p>
          
          <p className="text-lg leading-relaxed">
            Whether you have a few minutes to spare or want to spend hours building your capybara collection, <span className="font-semibold text-pink-300">Capybara Clicker 3D</span> offers a satisfying gaming experience that's completely free to enjoy online. No downloads or installations are required – simply open the game in your web browser and start clicking!
          </p>
        </div>
      </div>
    </section>
  );
};

export default GameDescription;
